import React, { useEffect, useRef, useState } from 'react';
import { useContracts } from '../../../context/ContractContext';
import Card from '../../common/Card';
import Button from '../../common/Button';
import Loader from '../../common/Loader';
import InlineLoader from '../../common/InlineLoader';
import { toBN, fromBN } from '../../../utils/format';
import { useTxRunner } from './AdminHelpers';

export default function ConfigOps() {
  const { main, mainW } = useContracts();
  const { busy, run } = useTxRunner();
  const [loading, setLoading] = useState(true);
  const [cur, setCur] = useState({});
  const mountedRef = useRef(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      try {
        const [
          roiRate, roiMax, cap, tz,
          minPkg, pkgMult, minW, wMult,
          bEn, bRate, bMult, bMax,
          t2Price
        ] = await Promise.all([
          main.roiRate(), main.roiMaxDays(), main.earningCapPct(), main.timezoneOffset(),
          main.minPackageAmount(), main.packageMultiple(), main.minimumWithdraw(), main.withdrawMultiple(),
          main.boosterEnabled(), main.boosterROIRate(), main.boosterMultiplier(), main.boosterMaxAmount(),
          main.token2PriceInUSDT()
        ]);
        if (!mountedRef.current) return;
        setCur({
          roiRate: roiRate.toString(),
          roiMax: roiMax.toString(),
          cap: cap.toString(),
          tz: tz.toString(),
          minPkg: fromBN(minPkg),
          pkgMult: fromBN(pkgMult),
          minW: fromBN(minW),
          wMult: fromBN(wMult),
          bEn, bRate: bRate.toString(), bMult: bMult.toString(), bMax: fromBN(bMax),
          t2Price: fromBN(t2Price)
        });
        setLoading(false);
      } catch (_) {
        if (mountedRef.current) setLoading(false);
      }
    };
    setLoading(true); load();
    return () => { mountedRef.current = false; };
  }, [main, refresh]);

  const refetch = () => setRefresh((v) => v + 1);
  const send = async (label, fn) => { const ok = await run(label, fn); if (ok) refetch(); };

  if (loading) return <InlineLoader size="lg" text="Loading current config..." />;

  return (
    <>
      {busy && <Loader text={busy} />}

      <div className="grid grid-2 mb-4">
        <Card title="ROI Config">
          <div className="text-dim mb-3" style={{ fontSize: 11 }}>
            Current: {cur.roiRate}/10000 · {cur.roiMax} days · Cap {cur.cap}%
          </div>
          <ConfigForm
            fields={[
              { key: 'rate', label: 'ROI Rate (basis pts, max 500)', type: 'number' },
              { key: 'maxDays', label: 'Max Days (max 1000)', type: 'number' }
            ]}
            onSubmit={(v) => send('Updating ROI', () => mainW.setROIConfig(v.rate, v.maxDays))}
            btnLabel="UPDATE ROI"
          />
        </Card>

        <Card title="Earning Cap %">
          <div className="text-dim mb-3" style={{ fontSize: 11 }}>Current: {cur.cap}% (100-1000)</div>
          <ConfigForm
            fields={[{ key: 'pct', label: 'Cap Percent', type: 'number' }]}
            onSubmit={(v) => send('Updating cap', () => mainW.setEarningCapPct(v.pct))}
            btnLabel="UPDATE CAP"
          />
        </Card>
      </div>

      <div className="grid grid-2 mb-4">
        <Card title="Package Config">
          <div className="text-dim mb-3" style={{ fontSize: 11 }}>Current: Min ${cur.minPkg} · Mult ${cur.pkgMult}</div>
          <ConfigForm
            fields={[
              { key: 'mult', label: 'Package Multiple (USD)', type: 'number' },
              { key: 'minA', label: 'Minimum Amount (USD)', type: 'number' }
            ]}
            transform={(v) => ({ mult: toBN(v.mult), minA: toBN(v.minA) })}
            onSubmit={(v) => send('Updating package', () => mainW.setPkgConfig(v.mult, v.minA))}
            btnLabel="UPDATE PACKAGE"
          />
        </Card>

        <Card title="Withdrawal Config">
          <div className="text-dim mb-3" style={{ fontSize: 11 }}>Current: Min ${cur.minW} · Mult ${cur.wMult}</div>
          <ConfigForm
            fields={[
              { key: 'minAmt', label: 'Minimum Withdraw (USD)', type: 'number' },
              { key: 'wMult', label: 'Withdraw Multiple (USD)', type: 'number' }
            ]}
            onSubmit={async (v) => {
              await send('Updating min withdraw', () => mainW.setMinWithdraw(toBN(v.minAmt)));
              await send('Updating withdraw multiple', () => mainW.setWithdrawMultiple(toBN(v.wMult)));
            }}
            btnLabel="UPDATE WITHDRAW"
          />
        </Card>
      </div>

      <div className="grid grid-2 mb-4">
        <Card title="Booster Config">
          <div className="text-dim mb-3" style={{ fontSize: 11 }}>
            Current: {cur.bEn ? 'ENABLED' : 'DISABLED'} · Rate {cur.bRate}/10000 · Mult {cur.bMult}x · Max ${cur.bMax}
          </div>
          <Button block variant={cur.bEn ? 'danger' : 'success'} onClick={() =>
            send(cur.bEn ? 'Disabling booster' : 'Enabling booster', () => mainW.setBoosterEnabled(!cur.bEn))
          }>{cur.bEn ? 'DISABLE BOOSTER' : 'ENABLE BOOSTER'}</Button>
          <div className="divider" />
          <ConfigForm
            fields={[
              { key: 'rate', label: 'Booster Rate (basis pts)', type: 'number' },
              { key: 'mult', label: 'Multiplier (x)', type: 'number' },
              { key: 'maxAmt', label: 'Max Amount (USD)', type: 'number' }
            ]}
            transform={(v) => ({ rate: v.rate, mult: v.mult, maxAmt: toBN(v.maxAmt) })}
            onSubmit={(v) => send('Updating booster', () => mainW.setBoosterConfig(v.rate, v.mult, v.maxAmt))}
            btnLabel="UPDATE BOOSTER"
          />
        </Card>

        <Card title="Token2 Price">
          <div className="text-dim mb-3" style={{ fontSize: 11 }}>Current: ${cur.t2Price} per Token2</div>
          <ConfigForm
            fields={[{ key: 'price', label: 'Price (USD per Token2)', type: 'number' }]}
            transform={(v) => ({ price: toBN(v.price) })}
            onSubmit={(v) => send('Updating price', () => mainW.updateToken2Price(v.price))}
            btnLabel="UPDATE PRICE"
          />
        </Card>
      </div>

      <Card title="Timezone Offset">
        <div className="text-dim mb-3" style={{ fontSize: 11 }}>
          Current: {cur.tz} seconds (±14h = ±50400)
        </div>
        <ConfigForm
          fields={[{ key: 'offset', label: 'Offset (seconds, -50400 to 50400)', type: 'number' }]}
          onSubmit={(v) => send('Updating timezone', () => mainW.setTimezoneOffset(v.offset))}
          btnLabel="UPDATE TIMEZONE"
        />
      </Card>
    </>
  );
}

function ConfigForm({ fields, onSubmit, btnLabel, transform }) {
  const [vals, setVals] = useState({});
  const setV = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const submit = () => {
    for (const f of fields) if (vals[f.key] === undefined || vals[f.key] === '') return;
    onSubmit(transform ? transform(vals) : vals);
    setVals({});
  };
  return (
    <>
      {fields.map((f) => (
        <div className="input-group" key={f.key}>
          <label className="input-label">{f.label}</label>
          <input className="input" type={f.type || 'text'} value={vals[f.key] ?? ''} onChange={(e) => setV(f.key, e.target.value)} />
        </div>
      ))}
      <Button block variant="primary" onClick={submit}>{btnLabel}</Button>
    </>
  );
}
